// RadarPlotFilters.js
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Select from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import RadarPlot from "../components/RadarPlot";
import {
  formateTimeRange,
  saveToState,
  loadFromState,
  resetState,
} from "../utils";
import { getProfiles, getNodes, getProfilerMetadata } from "../api/quontxApi";

const ITEMS_PER_PAGE = 25;

// Backend returns proxy profile under this uuid (shared across proxies)
const PROXY_PROFILE_UUID = "5666258c-3f67-4f16-8167-bff4a1cac760";

const RadarPlotFilters = ({ domains, isLoading, propagnateData }) => {
  const maxDomainCount = 12;
  const maxEntityCount = 5;

  const propagnateFunctionRef = useRef(propagnateData);

  // data keyed by SELECTED NODE UUID (entity uuid, proxy uuid)
  const [data, setData] = useState({});
  const [loadingData, setLoadingData] = useState(false);

  const [availableTimestampBuckets, setAvailableTimestampBuckets] = useState([]);
  const [activeTimestampBucket, setActiveTimestampBucket] = useState(null);

  const [activeDomains, setActiveDomains] = useState([]);
  const [activeEntities, setActiveEntities] = useState([]);
  const [activeProxies, setActiveProxies] = useState([]);

  const [domainSelectDisabled, setDomainSelectDisabled] = useState(false);
  const [entitySelectDisabled, setEntitySelectDisabled] = useState(false);
  const [proxySelectDisabled, setProxySelectDisabled] = useState(false);

  const hasAnySelected =
    (activeEntities?.length ?? 0) > 0 || (activeProxies?.length ?? 0) > 0;
  const hasProxySelected = (activeProxies?.length ?? 0) > 0;

  const toOption = (node, disabled) => ({
    label: node.name,
    value: node.uuid,
    isDisabled: disabled,
  });

  // ---------------------------
  // Selection management
  // ---------------------------

  const manageActiveDomains = useCallback(
    (selectedDomains) => {
      const safe = selectedDomains ?? [];
      if (safe.length <= maxDomainCount) {
        setDomainSelectDisabled(false);
        setActiveDomains(safe.map((d) => ({ label: d.label, value: d.value })));
        return;
      }
      setDomainSelectDisabled(true);
    },
    [maxDomainCount]
  );

  const manageActiveEntities = useCallback(
    (selectedEntities) => {
      const safe = selectedEntities ?? [];
      if (safe.length + (activeProxies?.length ?? 0) <= maxEntityCount) {
        setActiveEntities(safe.map((e) => ({ label: e.label, value: e.value })));
      }
    },
    [activeProxies, maxEntityCount]
  );

  const manageActiveProxies = useCallback(
    (selectedProxies) => {
      const safe = selectedProxies ?? [];
      if (safe.length + (activeEntities?.length ?? 0) <= maxEntityCount) {
        setActiveProxies(safe.map((p) => ({ label: p.label, value: p.value })));
      }
    },
    [activeEntities, maxEntityCount]
  );

  const resetFilters = useCallback(() => {
    resetState("radarPlotState", [
      { stateFunc: setActiveTimestampBucket, nullVal: null },
      { stateFunc: setActiveDomains, nullVal: [] },
      { stateFunc: setActiveEntities, nullVal: [] },
      { stateFunc: setActiveProxies, nullVal: [] },
    ]);

    setAvailableTimestampBuckets([]);
    setData({});
    setDomainSelectDisabled(false);
    setEntitySelectDisabled(false);
    setProxySelectDisabled(false);
  }, []);

  // ---------------------------
  // Server-side node search
  // ---------------------------

  const fetchNodesPage = useCallback(async ({ search, page, node_type }) => {
    const res = await getNodes({
      node_type,
      name: search,
      page,
      itemsPerPage: ITEMS_PER_PAGE,
    });
    return res?.data?.data ?? [];
  }, []);

  const loadEntityOptions = useCallback(
    async (search, _loadedOptions, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const items = await fetchNodesPage({
        search: q,
        page,
        node_type: "NAMED_ENTITY:ORGANISATION",
      });

      return {
        options: items.map((n) => toOption(n, entitySelectDisabled)),
        hasMore: items.length === ITEMS_PER_PAGE,
        additional: { page: page + 1 },
      };
    },
    [fetchNodesPage, entitySelectDisabled]
  );

  const loadProxyOptions = useCallback(
    async (search, _loadedOptions, additional) => {
      const q = (search || "").trim();
      if (q.length < 2) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }

      const page = additional?.page ?? 1;
      const items = await fetchNodesPage({
        search: q,
        page,
        node_type: "NODE_SET",
      });

      return {
        options: items.map((n) => toOption(n, proxySelectDisabled)),
        hasMore: items.length === ITEMS_PER_PAGE,
        additional: { page: page + 1 },
      };
    },
    [fetchNodesPage, proxySelectDisabled]
  );

  const entityAdditional = useMemo(() => ({ page: 1 }), []);
  const proxyAdditional = useMemo(() => ({ page: 1 }), []);

  // ---------------------------
  // Load timestamp buckets for selected proxies
  // (must use REAL proxy uuids)
  // ---------------------------

  const loadTimestampBucketsForProxies = useCallback(async () => {
    const proxyIds = (activeProxies ?? []).map((p) => p.value);

    if (proxyIds.length === 0) {
      setAvailableTimestampBuckets([]);
      return;
    }

    const res = await getProfilerMetadata({
      itemsPerPage: 100,
      entities: proxyIds, // REAL proxy ids
      node_type: "NODE_SET",
    });

    const rows = res?.data?.data ?? res?.data ?? [];
    const buckets = [];
    rows.forEach((row) => {
      buckets.push(...(row?.metadata?.corpora?.time_intervals ?? []));
    });

    setAvailableTimestampBuckets(Array.from(new Set(buckets)));
  }, [activeProxies]);

  useEffect(() => {
    loadTimestampBucketsForProxies();
  }, [loadTimestampBucketsForProxies]);

  // Auto-select default bucket when proxies selected
  useEffect(() => {
    if (!hasProxySelected) return;
    if (!availableTimestampBuckets.length) return;

    if (
      activeTimestampBucket &&
      availableTimestampBuckets.includes(activeTimestampBucket)
    ) {
      return;
    }

    const defaultBucket =
      availableTimestampBuckets[10] ?? availableTimestampBuckets[0] ?? null;

    if (defaultBucket) setActiveTimestampBucket(defaultBucket);
  }, [hasProxySelected, availableTimestampBuckets, activeTimestampBucket]);

  useEffect(() => {
    if (
      activeTimestampBucket &&
      !availableTimestampBuckets.includes(activeTimestampBucket)
    ) {
      setActiveTimestampBucket(null);
    }
  }, [availableTimestampBuckets, activeTimestampBucket]);

  // ---------------------------
  // Load profiles (IMPORTANT FIX)
  // - request REAL proxy uuids
  // - map returned shared proxy profile onto each proxy uuid
  // ---------------------------

  const loadProfiles = useCallback(async () => {
    const entityIds = activeEntities.map((e) => e.value);
    const proxyIds = activeProxies.map((p) => p.value);

    // ✅ IMPORTANT: request the REAL uuids (NO hardcoded uuid here)
    const requestIds = [...entityIds, ...proxyIds];

    if (requestIds.length === 0) {
      setData({});
      return;
    }

    const profiles = await getProfiles({
      itemsPerPage: requestIds.length,
      entities: requestIds,
    });

    propagnateFunctionRef.current(profiles.data);

    const rows = profiles?.data?.data ?? [];

    // index returned profiles by uuid
    const profileByUuid = {};
    for (const r of rows) {
      const uuid = r?.metadata?.corpora?.uuid;
      if (uuid) profileByUuid[uuid] = r.values;
    }

    // build final data keyed by SELECTED UUIDs
    const next = {};

    // entities: use own uuid (must exist in response under their uuid)
    for (const id of entityIds) {
      if (profileByUuid[id]) next[id] = profileByUuid[id];
      else {
        console.warn("[RadarPlotFilters] Missing entity profile", {
          id,
          availableKeys: Object.keys(profileByUuid),
        });
      }
    }

    // proxies: backend may return the shared proxy profile under PROXY_PROFILE_UUID
    // so we map that onto every selected proxy uuid
    if (proxyIds.length > 0) {
      const sharedProxyProfile = profileByUuid[PROXY_PROFILE_UUID];

      if (!sharedProxyProfile) {
        console.warn("[RadarPlotFilters] Missing shared proxy profile in response", {
          expectedSharedUuid: PROXY_PROFILE_UUID,
          requestedProxyIds: proxyIds,
          availableKeys: Object.keys(profileByUuid),
        });
      } else {
        for (const proxyId of proxyIds) next[proxyId] = sharedProxyProfile;
      }
    }

    setData(next);
  }, [activeEntities, activeProxies]);

  // Enable/disable selects based on maxEntityCount
  useEffect(() => {
    const totalSelected =
      (activeEntities?.length ?? 0) + (activeProxies?.length ?? 0);
    if (totalSelected < maxEntityCount) {
      setEntitySelectDisabled(false);
      setProxySelectDisabled(false);
    } else {
      setEntitySelectDisabled(true);
      setProxySelectDisabled(true);
    }
  }, [activeEntities, activeProxies, maxEntityCount]);

  // ---------------------------
  // Restore/persist localStorage
  // ---------------------------

  useEffect(() => {
    loadFromState("radarPlotState", [
      {
        stateFunc: setActiveTimestampBucket,
        storageObjectKey: "activeTimestampBucket",
        defaultVal: null,
      },
      {
        stateFunc: setActiveDomains,
        storageObjectKey: "activeDomains",
        defaultVal: [],
      },
      {
        stateFunc: setActiveEntities,
        storageObjectKey: "activeEntities",
        defaultVal: [],
      },
      {
        stateFunc: setActiveProxies,
        storageObjectKey: "activeProxies",
        defaultVal: [],
      },
    ]);

    const fn = propagnateFunctionRef.current;
    return () => fn({});
  }, []);

  useEffect(() => {
    if (
      activeTimestampBucket !== null ||
      activeDomains.length > 0 ||
      activeEntities.length > 0 ||
      activeProxies.length > 0
    ) {
      saveToState("radarPlotState", {
        activeTimestampBucket,
        activeDomains,
        activeEntities,
        activeProxies,
      });
    }
  }, [activeTimestampBucket, activeDomains, activeEntities, activeProxies]);

  // ---------------------------
  // Trigger profile fetch
  // ---------------------------

  useEffect(() => {
    const totalSelected =
      (activeEntities?.length ?? 0) + (activeProxies?.length ?? 0);

    if (totalSelected === 0) {
      setData({});
      return;
    }

    (async () => {
      setLoadingData(true);
      try {
        await loadProfiles();
      } finally {
        setLoadingData(false);
      }
    })();
  }, [activeEntities, activeProxies, loadProfiles]);

  const rawTimebucketIdx = availableTimestampBuckets.indexOf(activeTimestampBucket);
  const timebucketIdx = hasProxySelected ? rawTimebucketIdx : 0;

  return (
    <div className="w-full flex flex-col items-center content-center">
      {isLoading && <p>Loading data...</p>}

      {!isLoading && domains && (
        <div>
          <div className="flex flex-col gap-4 items-center justify-center mb-8">
            <div className="flex gap-4 justify-center mb-8">
              <div className="w-[240px] flex flex-col gap-2">
                <p>Organization Profiles</p>
                <AsyncPaginate
                  isMulti
                  value={activeEntities}
                  onChange={manageActiveEntities}
                  loadOptions={loadEntityOptions}
                  additional={entityAdditional}
                  debounceTimeout={250}
                  cacheOptions
                  placeholder="Type to search…"
                />
              </div>

              <div className="w-[240px] flex flex-col gap-2">
                <p>Organization Proxies</p>
                <AsyncPaginate
                  isMulti
                  value={activeProxies}
                  onChange={manageActiveProxies}
                  loadOptions={loadProxyOptions}
                  additional={proxyAdditional}
                  debounceTimeout={250}
                  cacheOptions
                  placeholder="Type to search…"
                />
              </div>
            </div>

            <div className="flex gap-4 justify-center mb-8">
              <div className="w-[240px] flex flex-col gap-2">
                <p>Societal Domains</p>
                <Select
                  isDisabled={!hasAnySelected}
                  isMulti
                  isSearchable
                  value={activeDomains}
                  onChange={manageActiveDomains}
                  options={(domains ?? []).map((domain) => ({
                    label: domain.description,
                    value: domain.uuid,
                    isDisabled: domainSelectDisabled,
                  }))}
                />
              </div>

              <div className="w-[240px] flex flex-col gap-2">
                <p>Timestamp Bucket</p>
                <Select
                  isDisabled={
                    !hasAnySelected ||
                    (hasProxySelected && availableTimestampBuckets.length === 0)
                  }
                  isSearchable={false}
                  value={
                    activeTimestampBucket
                      ? { label: activeTimestampBucket, value: activeTimestampBucket }
                      : null
                  }
                  onChange={(selected) => setActiveTimestampBucket(selected.value)}
                  options={availableTimestampBuckets.map((tb, idx) => ({
                    label: formateTimeRange(tb),
                    value: tb,
                    isDisabled: idx < 10,
                  }))}
                />
              </div>
            </div>

            <button
              onClick={resetFilters}
              className="bg-blue-500 text-white font-semibold py-2 px-4 rounded hover:bg-blue-600 transition duration-200"
            >
              Reset filters
            </button>
          </div>

          {activeDomains?.length >= 3 &&
            hasAnySelected &&
            (!hasProxySelected || (activeTimestampBucket && timebucketIdx >= 0)) &&
            Object.keys(data ?? {}).length > 0 && (
              <div className="flex items-center content-center flex-wrap">
                <RadarPlot
                  isLoading={loadingData}
                  data={data}
                  timebucketIdx={timebucketIdx}
                  activeDomains={activeDomains}
                  activeEntities={activeEntities}
                  activeProxies={activeProxies}
                />
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default RadarPlotFilters;